// Avatar + platform-chip primitives for the Brand Protection mentions feed.
import React, { useEffect, useState } from 'react';
import { PLATFORM_ICON, unwrap } from './_shared';
import { bp_resolve_avatar } from '../../../services';

// Module-level cache so the same (platform, handle) pair only triggers one
// network call across every card / page / scroll in the session. Keys are
// lowercased; values are either a resolved URL string, the literal '' for
// "resolved but no image", or a pending Promise so concurrent renders share
// the in-flight request.
const _avatarCache = new Map();
function cacheKey(platform, handle) {
    return `${(platform || '').toLowerCase()}:${(handle || '').toLowerCase()}`;
}
function pickAvatarUrl(d) {
    if (!d) return '';
    // Backend marks `is_default: true` / `status: "blocked"` when it couldn't
    // scrape the real profile picture and is handing back a generic brand
    // icon. Treat those as "no avatar" so the UI falls through to the
    // platform-disc fallback instead of rendering a black-on-dark icon that
    // most ad-blockers also strip.
    if (d.is_default || d.status === 'blocked') return '';
    return d.avatar_url || d.url || d.image_url || d.image || d.avatar || '';
}
function fetchAvatar(platform, handle) {
    const key = cacheKey(platform, handle);
    const hit = _avatarCache.get(key);
    if (typeof hit === 'string') return Promise.resolve(hit);
    if (hit && typeof hit.then === 'function') return hit;
    const p = bp_resolve_avatar({ platform, handle })
        .then(res => {
            const d = unwrap(res) || {};
            const url = pickAvatarUrl(d);
            _avatarCache.set(key, url);
            return url;
        })
        .catch(() => {
            _avatarCache.set(key, '');
            return '';
        });
    _avatarCache.set(key, p);
    return p;
}

// Platform → canonical profile URL template.
const PROFILE_URL = {
    x: (h) => `https://x.com/${h.replace(/^@/, '')}`,
    twitter: (h) => `https://x.com/${h.replace(/^@/, '')}`,
    reddit: (h) => `https://www.reddit.com/user/${h.replace(/^u\//, '')}`,
    bluesky: (h) => `https://bsky.app/profile/${h.replace(/^@/, '')}`,
    linkedin: (h) => `https://www.linkedin.com/in/${h.replace(/^@/, '')}`,
    facebook: (h) => `https://www.facebook.com/${h.replace(/^@/, '')}`,
    instagram: (h) => `https://www.instagram.com/${h.replace(/^@/, '')}`,
    youtube: (h) => `https://www.youtube.com/@${h.replace(/^@/, '')}`,
    tiktok: (h) => `https://www.tiktok.com/@${h.replace(/^@/, '')}`,
};

function isMaskedLabel(handle) {
    return /[\s#•]/.test(handle || '');
}

export function profileUrlFor(handle, platform) {
    if (!handle || !platform) return null;
    if (isMaskedLabel(handle)) return null;
    const fn = PROFILE_URL[platform?.toLowerCase()];
    if (!fn) return null;
    try { return fn(handle); } catch { return null; }
}

// Renders a circular avatar. Falls back to a coloured initial if no image URL
// is available or if the remote image fails to load. When `avatarUrl` isn't
// passed explicitly, calls the brand-protection avatar resolver with the
// handle+platform pair (deduped via _avatarCache so same handle = one call).
export function AccountAvatar({ handle, platform, avatarUrl, handleToken, size = 40 }) {
    const [imgError, setImgError] = useState(false);
    const lookupHandle = handleToken || handle;
    const [resolvedUrl, setResolvedUrl] = useState(() => {
        if (avatarUrl) return avatarUrl;
        const hit = _avatarCache.get(cacheKey(platform, lookupHandle));
        return typeof hit === 'string' ? hit : '';
    });

    useEffect(() => {
        if (avatarUrl || !lookupHandle || !platform) return;
        let alive = true;
        fetchAvatar(platform, lookupHandle).then(url => {
            if (alive && url) setResolvedUrl(url);
        });
        return () => { alive = false; };
    }, [lookupHandle, platform, avatarUrl]);

    const effectiveUrl = avatarUrl || resolvedUrl;
    const initial = (handle || '?').replace(/^[@u/]/, '').charAt(0).toUpperCase();

    // Stable colour from handle string so the same account always gets the same hue.
    const hue = [...(handle || 'X')].reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360;

    const style = {
        width: size,
        height: size,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: Math.round(size * 0.4),
        fontWeight: 600,
        backgroundColor: `hsl(${hue}, 55%, 40%)`,
        color: '#fff',
        userSelect: 'none',
        overflow: 'hidden',
    };

    if (effectiveUrl && !imgError) {
        return (
            <span style={style}>
                <img
                    src={effectiveUrl}
                    alt={handle || 'avatar'}
                    onError={() => setImgError(true)}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                />
            </span>
        );
    }

    // No real avatar available - fall back to a platform-tinted disc with
    // the platform icon. Looks closer to Brand24 than a coloured initial
    // and reads correctly when most rows share the same platform.
    if (platform) {
        return <PlatformAvatarDisc platform={platform} size={size} />;
    }

    return <span style={style}>{initial}</span>;
}

// Per-platform brand identity. `color` is the canonical brand colour (used
// for the icon glyph and to derive the bg / border tints). `path` is the
// SVG path inside a 24×24 viewBox - sourced from simpleicons.org so the
// logo always matches what the user sees on the platform itself. Keeping
// the SVGs inline (vs. fetching from a CDN) avoids ad-blocker / CORS
// failures that bite cdn.simpleicons.org in the wild.
const PLATFORM_BRAND = {
    x: {
        color: '#0f172a',
        path: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z',
    },
    twitter: {
        color: '#1DA1F2',
        path: 'M21.543 7.104c.015.211.015.423.015.636 0 6.507-4.954 14.01-14.01 14.01v-.003A13.94 13.94 0 0 1 0 19.539a9.88 9.88 0 0 0 7.287-2.041 4.93 4.93 0 0 1-4.6-3.42 4.916 4.916 0 0 0 2.223-.084A4.926 4.926 0 0 1 .96 9.167v-.062a4.887 4.887 0 0 0 2.235.616A4.928 4.928 0 0 1 1.67 3.148 13.98 13.98 0 0 0 11.82 8.292a4.929 4.929 0 0 1 8.39-4.49 9.868 9.868 0 0 0 3.128-1.196 4.941 4.941 0 0 1-2.165 2.724A9.828 9.828 0 0 0 24 4.555a10.019 10.019 0 0 1-2.457 2.549z',
    },
    reddit: {
        color: '#FF4500',
        path: 'M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.985 0 1.788.803 1.788 1.79 0 .724-.435 1.343-1.053 1.622-.028.18-.042.36-.042.54 0 2.755-3.21 4.987-7.166 4.987-3.957 0-7.167-2.232-7.167-4.987 0-.18-.014-.36-.041-.54-.619-.279-1.054-.897-1.054-1.621 0-.987.803-1.79 1.79-1.79.476 0 .897.182 1.205.49 1.207-.87 2.878-1.43 4.717-1.487l.9-4.23a.342.342 0 0 1 .413-.262l2.93.622a1.25 1.25 0 0 1 1.21-.96zM8.706 12.5a1.25 1.25 0 0 0-1.25 1.25 1.25 1.25 0 0 0 1.25 1.25c.69 0 1.249-.56 1.249-1.25-.001-.69-.559-1.249-1.249-1.249zm6.587 0c-.69 0-1.249.559-1.249 1.25s.559 1.25 1.249 1.25a1.25 1.25 0 0 0 1.25-1.25 1.25 1.25 0 0 0-1.25-1.25zm-4.27 3.953a.33.33 0 0 0-.236.1.337.337 0 0 0 0 .48c.79.79 2.301.85 2.737.85.435 0 1.946-.06 2.737-.85a.337.337 0 0 0 .035-.443.33.33 0 0 0-.27-.137c-.066 0-.13.022-.184.07-.5.501-1.566.677-2.318.677-.751 0-1.818-.176-2.318-.677a.33.33 0 0 0-.183-.07z',
    },
    bluesky: {
        color: '#1185FE',
        path: 'M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.911.58-7.392 2.005-2.83 7.078 5.018 5.19 6.88-1.113 7.833-4.308.953 3.195 2.05 9.271 7.733 4.308 4.272-4.308 1.183-6.498-2.728-7.078a8.547 8.547 0 0 1-.415-.056c.14.017.279.036.415.056 2.67.296 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8Z',
    },
    linkedin: {
        color: '#0A66C2',
        path: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z',
    },
    facebook: {
        color: '#1877F2',
        path: 'M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z',
    },
    instagram: {
        color: '#E4405F',
        path: 'M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z',
    },
    youtube: {
        color: '#FF0000',
        path: 'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z',
    },
    tiktok: {
        color: '#010101',
        path: 'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z',
    },
    telegram: {
        color: '#26A5E4',
        path: 'M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z',
    },
};

// Derive a "very light" background and a slightly darker border from the
// brand hex. Returned values are rgba so the disc stays semi-transparent
// against whatever card colour wraps it.
function tintsForColor(hex) {
    const h = (hex || '#64748b').replace('#', '');
    const full = h.length === 3
        ? h.split('').map(c => c + c).join('')
        : h.padEnd(6, '0').slice(0, 6);
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    return {
        bg: `rgba(${r}, ${g}, ${b}, 0.12)`,
        border: `rgba(${r}, ${g}, ${b}, 0.32)`,
    };
}

// Circular disc showing the platform's real brand logo - use where a user
// avatar would appear but the entity is a platform/source rather than a
// person (or when the real profile picture can't be resolved).
export function PlatformAvatarDisc({ platform, size = 40 }) {
    if (!platform) return null;
    const key = platform.toLowerCase();
    const brand = PLATFORM_BRAND[key];
    const fallbackGlyph = PLATFORM_ICON[key] || platform.charAt(0).toUpperCase();
    const color = brand ? brand.color : '#64748b';
    const { bg, border } = tintsForColor(color);
    return (
        <span
            title={platform}
            style={{
                width: size,
                height: size,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: bg,
                border: `1px solid ${border}`,
                color,
                fontSize: Math.round(size * 0.42),
                fontWeight: 600,
                userSelect: 'none',
                boxSizing: 'border-box',
            }}
        >
            {brand ? (
                <svg
                    viewBox="0 0 24 24"
                    width={Math.round(size * 0.55)}
                    height={Math.round(size * 0.55)}
                    fill={color}
                    aria-hidden="true"
                >
                    <path d={brand.path} />
                </svg>
            ) : (
                fallbackGlyph
            )}
        </span>
    );
}

// Small pill chip showing the platform label / icon.
export function PlatformChip({ platform }) {
    if (!platform) return null;
    const key = platform.toLowerCase();
    const icon = PLATFORM_ICON[key] || platform.charAt(0).toUpperCase();
    return (
        <span
            title={platform}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
                padding: '1px 7px',
                borderRadius: 10,
                fontSize: 12,
                fontWeight: 800,
                background: 'rgba(59,130,246,0.13)',
                color: '#010a15',
                border: '1px solid rgba(59,130,246,0.3)',
                lineHeight: 1.6,
            }}
        >
            {icon}
        </span>
    );
}
